import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import DashboardUi from './ui/dashboard.ui';
import { useJobRole } from '../context/job-role.context';
import { RootStoreContext } from '../core/root-store';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { signOut } from '../user/user-update.service';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { updateUserProfileInDatabase } from '../user/user.service';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase/config';
import { UserRole } from '../user/user.type';

dayjs.extend(utc);

export enum FounderAgreementStatus {
  Pending,
  Declined,
  Agreed,
}

const DashboardContainer = observer(() => {
  const { jobRoleData } = useJobRole();
  const rootStore = React.useContext(RootStoreContext);
  const appliedFor = rootStore.userStore.user?.webOnboarding?.appliedFor;
  const hasAppliedToJob = appliedFor !== '';
  const userRole = rootStore.userStore.user?.role;
  const isMeetingInviteSent = rootStore.userStore.user?.webOnboarding?.meetingInviteSent || false;

  const [preferredTime, setPreferredTime] = useState<Dayjs | null>(null);
  const [alternativeTime, setAlternativeTime] = useState<Dayjs | null>(null);
  const [meetingInviteLoading, setMeetingInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const getInitialAgreementStatus = (): FounderAgreementStatus => {
    const hasAgreedToProTerms = rootStore.userStore.user?.hasAgreedToProTerms;
    if (hasAgreedToProTerms === null || hasAgreedToProTerms === undefined) {
      return FounderAgreementStatus.Pending;
    }
    return hasAgreedToProTerms ? FounderAgreementStatus.Agreed : FounderAgreementStatus.Declined;
  };

  const [founderAgreementStatus, setFounderAgreementStatus] = useState<FounderAgreementStatus>(getInitialAgreementStatus());

  useEffect(() => {
    if (userRole === UserRole.Founder && rootStore.userStore.user) {
      rootStore.onboardingStore.markWelcomeAgreementSeen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, rootStore.onboardingStore]);

  const handleCreateInvite = async () => {
    if (!preferredTime || !alternativeTime) {
      return;
    }

    if (!rootStore.userStore.user?.uid || !jobRoleData?.founderId) {
      return;
    }

    const functions = getFunctions();
    const createInvitationFunc = httpsCallable(functions, 'createInvitationWithUIDsAndTimes');

    const payload = {
      senderId: rootStore.userStore.user.uid,
      recipientId: jobRoleData.founderId,
      preferredDateTime: preferredTime.valueOf(),
      alternativeDateTime: alternativeTime.valueOf(),
    };

    try {
      setMeetingInviteLoading(true);
      setInviteError(null);
      await createInvitationFunc(payload);

      if (rootStore.userStore.user.webOnboarding) {
        await updateUserProfileInDatabase(rootStore.userStore.user.uid, {
          webOnboarding: {
            ...rootStore.userStore.user.webOnboarding,
            meetingInviteSent: true,
          },
        });

        rootStore.userStore.updateUserData({
          webOnboarding: {
            ...rootStore.userStore.user.webOnboarding,
            meetingInviteSent: true,
          },
        });
      }

      setMeetingInviteLoading(false);
    } catch (error: any) {
      logEvent(analytics, 'meeting_invite_error', {
        error_code: error?.code || 'unknown',
        error_message: error?.message || 'Unknown error',
        user_id: rootStore.userStore.user?.uid || 'unknown',
        sender_id: payload.senderId,
        recipient_id: payload.recipientId,
      });

      setMeetingInviteLoading(false);

      let errorMessage = 'Failed to send meeting invite. Please try again or contact support if the problem persists.';

      if (error?.code === 'functions/not-found') {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      } else if (error?.code === 'functions/unauthenticated') {
        errorMessage = 'Please log in again to send the invite.';
      } else if (error?.code === 'functions/permission-denied') {
        errorMessage = 'You do not have permission to send meeting invites.';
      } else if (error?.code === 'functions/unavailable') {
        errorMessage = 'Service is currently unavailable. Please check your internet connection and try again.';
      } else if (error?.message?.includes('network')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error?.message?.includes('CORS') || error?.message?.includes('blocked')) {
        errorMessage = 'Request was blocked. Please disable browser extensions (like ad blockers) and try again.';
      } else if (error?.code === 'functions/deadline-exceeded') {
        errorMessage = 'Request timed out. Please try again.';
      }

      setInviteError(errorMessage);
    }
  };

  const handleFounderAgreementChange = (status: FounderAgreementStatus) => {
    setFounderAgreementStatus(status);
    const hasAgreed = status === FounderAgreementStatus.Agreed;
    rootStore.onboardingStore.updateFounderAgreementStatus(hasAgreed);
  };

  const handleLogout = async () => {
    try {
      // Store user role for redirect purposes
      if (userRole) {
        sessionStorage.setItem('lastUserRole', userRole);
      }

      await signOut();
    } catch (error) {}
  };

  return (
    <DashboardUi
      preferredTime={preferredTime}
      alternativeTime={alternativeTime}
      userRole={userRole}
      isMeetingInviteSent={isMeetingInviteSent}
      hasAppliedToJob={hasAppliedToJob}
      setPreferredTime={setPreferredTime}
      setAlternativeTime={setAlternativeTime}
      meetingInviteLoading={meetingInviteLoading}
      inviteError={inviteError}
      founderAgreementStatus={founderAgreementStatus}
      setFounderAgreementStatus={handleFounderAgreementChange}
      onLogout={handleLogout}
      onCreateInvite={handleCreateInvite}
    />
  );
});

export default DashboardContainer;
